/**
 * Growth Adjustment Card for Accountant Report View
 * Displays the 4-step growth adjustment calculation
 */

import React from 'react';
import CalculationMethodology from '../CalculationMethodology';

interface GrowthAdjustment {
  claimant_factor_pct?: number;
  general_factor_pct?: number;
  total_factor_pct?: number;
  incremental_lost_profit?: number;
}

interface GrowthAdjustmentCardProps {
  growthAdjustment: GrowthAdjustment;
  benchmarkRevenue?: number;
  variableMarginPct?: number;
  financialData?: any;
  formatCurrency: (amount?: number) => string;
  formatPercent: (value?: number) => string;
}

export default function GrowthAdjustmentCard({
  growthAdjustment,
  benchmarkRevenue,
  variableMarginPct,
  financialData,
  formatCurrency,
  formatPercent
}: GrowthAdjustmentCardProps) {
  if (!growthAdjustment) return null;

  // Calculate historical revenues for methodology explanation
  const historicalRevenues = financialData?.yearly_data ? 
    Object.entries(financialData.yearly_data)
      .sort(([yearA], [yearB]) => parseInt(yearA) - parseInt(yearB))
      .map(([year, months]: [string, any], idx, arr) => {
        const yearRevenue = months.reduce((sum: number, m: any) => sum + (m.revenue || 0), 0);
        const prevYearRevenue = idx > 0 ? arr[idx - 1][1].reduce((sum: number, m: any) => sum + (m.revenue || 0), 0) : null;
        const changePct = prevYearRevenue ? ((yearRevenue - prevYearRevenue) / prevYearRevenue) * 100 : null;
        return {
          year: parseInt(year),
          revenue: yearRevenue,
          change_pct: changePct
        };
      })
    : [];

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
      <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
        <span className="text-2xl mr-2">📈</span>
        Growth Adjustment (Step 2)
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Step 1: Claimant Factor */}
        <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-gray-400">
          <p className="text-xs text-gray-600 font-medium mb-1">STEP 1</p>
          <p className="text-sm text-gray-700 mb-2 font-semibold">Claimant Factor</p>
          <p className="text-2xl font-bold text-gray-900 mb-2">
            {formatPercent(growthAdjustment.claimant_factor_pct)}
          </p>
          <div className="text-xs text-gray-700 bg-gray-200 rounded px-2 py-1">
            Historical trend regression
          </div>
        </div>

        {/* Step 2: General Factor */}
        <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-gray-400">
          <p className="text-xs text-gray-600 font-medium mb-1">STEP 2</p>
          <p className="text-sm text-gray-700 mb-2 font-semibold">General Factor</p>
          <p className="text-2xl font-bold text-gray-900 mb-2">
            {formatPercent(growthAdjustment.general_factor_pct)}
          </p>
          <div className="text-xs text-gray-700 bg-gray-200 rounded px-2 py-1">
            Industry benchmark
          </div>
        </div>

        {/* Step 3: Total Factor */}
        <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-600">
          <p className="text-xs text-blue-700 font-medium mb-1">STEP 3: SUM</p>
          <p className="text-sm text-blue-800 mb-2 font-semibold">Total Factor</p>
          <p className="text-2xl font-bold text-blue-900 mb-2">
            {formatPercent(growthAdjustment.total_factor_pct)}
          </p>
          <div className="text-xs text-blue-700 bg-blue-100 rounded px-2 py-1 font-mono">
            {formatPercent(growthAdjustment.claimant_factor_pct)} + {formatPercent(growthAdjustment.general_factor_pct)}
          </div>
        </div>

        {/* Step 4: Incremental Profit */}
        <div className="bg-green-50 rounded-lg p-4 border-l-4 border-green-600">
          <p className="text-xs text-green-700 font-medium mb-1">STEP 4: RESULT</p>
          <p className="text-sm text-green-800 mb-2 font-semibold">Incremental Profit</p>
          <p className="text-2xl font-bold text-green-900 mb-2">
            {formatCurrency(growthAdjustment.incremental_lost_profit)}
          </p>
          <div className="text-xs text-green-700 bg-green-100 rounded px-2 py-1 font-mono">
            ({formatCurrency(benchmarkRevenue)} × {formatPercent(growthAdjustment.total_factor_pct)}) × {formatPercent(variableMarginPct)}
          </div>
        </div>
      </div>
      
      {/* Methodology Explanation */}
      <CalculationMethodology 
        section="growth_adjustment" 
        data={{
          ...growthAdjustment,
          benchmark_revenue: benchmarkRevenue,
          variable_margin_pct: variableMarginPct,
          but_for_revenue: benchmarkRevenue ? benchmarkRevenue * (1 + (growthAdjustment.total_factor_pct || 0) / 100) : undefined,
          incremental_revenue: benchmarkRevenue ? (benchmarkRevenue * (1 + (growthAdjustment.total_factor_pct || 0) / 100)) - benchmarkRevenue : undefined,
          historical_revenues: historicalRevenues
        }} 
      />
    </div>
  );
}
